import { ThumbsUp, ThumbsDown, Trash2 } from 'lucide-react';
import { useState } from 'react';
import DeleteConfirmModal from './DeleteConfirmModal';
import { isUserReport } from './reportStorageUtils';

const ReportItem = ({ report, companyName, onDelete }) => {
  const [showFullId, setShowFullId] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [lastTouchTime, setLastTouchTime] = useState(0);

  const userOwnsReport = isUserReport(report.reportId);
  const sentimentIcon = report.vote > 0 
    ? <ThumbsUp className="w-5 h-5 text-green-500" /> 
    : <ThumbsDown className="w-5 h-5 text-red-500" />;

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown Date';
    const date = new Date(timestamp * 1000);
    return date.toISOString().split('T')[0];
  };

  const handleTouchStart = () => {
    const now = Date.now();
    if (now - lastTouchTime < 300) {
      setShowFullId(true);
      setTimeout(() => setShowFullId(false), 3000);
    }
    setLastTouchTime(now);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(report.reportId, companyName);
    setIsDeleting(false);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div className="border dark:border-gray-700 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
        <div className="flex justify-between items-start mb-2">
          <div 
            className="text-sm text-gray-500 dark:text-gray-400 cursor-pointer select-none transition-all duration-200 hover:text-gray-700 dark:hover:text-gray-300 date-field touch-target no-select"
            onMouseEnter={() => setShowFullId(true)}
            onMouseLeave={() => setShowFullId(false)}
            onTouchStart={handleTouchStart}
          >
            {showFullId ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded border border-blue-200 dark:border-blue-700">
                <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">Full Report ID:</div>
                <span className="font-mono text-xs break-all text-blue-800 dark:text-blue-200">
                  {report.reportId}
                </span>
              </div>
            ) : (
              <span className="inline-flex items-center">
                📅 {formatDate(report.createdAt)}
              </span>
            )}
          </div>
          <div>{sentimentIcon}</div>
        </div>
        <div className="flex justify-between items-end">
          <div className="flex-1">
            <h4 className="font-bold">AI Application:</h4>
            <p className="mb-2">{report.aiUse}</p>
            <h4 className="font-bold">Reported Impact:</h4>
            <p>{report.impact}</p>
            {report.url && (
              <div className="mt-3">
                <a href={report.url} target="_blank" rel="https://ai-transparency.eu" className="text-blue-600 hover:underline break-all">
                  View supporting source(s)
                </a>
              </div>
            )}
          </div>
          {userOwnsReport && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              title="Delete this report"
              className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-50 ml-4"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        isDeleting={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  );
};

export default ReportItem;
